// DS — mini loga platform W BARWACH MAREK (uwaga właściciela z 21.09 wieczór, paczka P1 A).
//
// Dosłownie: „wszędzie tam gdzie są te loga tiktoka ig yt, te takie mini ikonki: niech one
// będą normalnymi mini logami, ich kolory przyciągają uwagę z daleka”. Do 21.09 trzy znaki
// były jednokolorowymi glifami kreskowymi w `currentColor` (sesja 20 R2, decyzja d) — z metra
// nie do odróżnienia od siebie i od reszty zestawu. Kolor JEST tu informacją: konto poznaje
// się po barwie znaku, zanim przeczyta się uchwyt.
//
// JEDEN RYSUNEK NA CAŁY PRODUKT. Ten plik jest jedynym miejscem, w którym te trzy znaki są
// narysowane; `ds/media/Glyph` (aplikacja) i `components/public/glyphs37` (strona publiczna)
// oddają mu nazwy platform, więc żadne z nich nie trzyma własnej kopii ścieżek.
//
// Siatka 48 — ta sama co w `Glyph`, więc podmiana znaku NIE rusza układu żadnego ekranu:
// `size` zostaje dokładnie tym, czym był (10 · 12 · 14 · 16 · 20 · 24 · 40), a bez `size`
// znak wypełnia rodzica (tak wołają go grafiki strony publicznej).
//
// Barwy stoją w `ds/tokens.css` jako `--brand-*` — jedyny wyjątek od zakazu surowych kolorów
// poza tokenami, bo to cudze znaki towarowe, a nie paleta produktu. Nuta TikToka czyta
// `--brand-tiktok-note`, które na ciemnych powierzchniach (`.sidebar`, `.mp-dark`, `.ds-selbar`)
// przechodzi w biel — czarna nuta na ciemnym tle znika.
//
// Dostępność: znak obok nazwy platformy albo uchwytu konta jest DEKORACJĄ (`aria-hidden`),
// bo nazwa już tam stoi; stojąc sam dostaje `aria-label`. Konto rozłączone dostaje TEN SAM
// znak z obniżonym nasyceniem (`muted`), nigdy powrót do kreski — szary glif kreskowy czytał
// się jak inna platforma, a nie jak ten sam profil w innym stanie.

import { useId } from 'react'
import type { SVGProps } from 'react'

export type PlatformLogoName = 'tiktok' | 'instagram' | 'youtube'

export const PLATFORM_LOGO_NAMES: PlatformLogoName[] = ['tiktok', 'instagram', 'youtube']

/** Nazwa platformy albo nic — platforma spoza zestawu nie dostaje cudzego logo. */
export function platformLogoName(platform?: string | null): PlatformLogoName | null {
  const key = (platform ?? '').toLowerCase()
  return (PLATFORM_LOGO_NAMES as string[]).includes(key) ? (key as PlatformLogoName) : null
}

/**
 * Nuta TikToka: DWIE ścieżki rysowane TRZY razy — cyjan w lewo-górę, czerwień w prawo-dół,
 * nuta właściwa na wierzchu. To jest cały znak marki; kształt jest ten sam co w glifie
 * kreskowym sprzed 21.09 (siatka 48), więc rozmiary i odstępy na ekranach zostają.
 */
const TIKTOK_NOTE = ['M28 9v20a8 8 0 1 1-8-8', 'M28 9c1.2 6.4 5.4 9.6 10.5 9.6']
const TIKTOK_SHIFT = 2.6

function TikTok() {
  return (
    <g strokeWidth={6} strokeLinecap="round" strokeLinejoin="round" fill="none">
      {([
        ['var(--brand-tiktok-cyan)', `translate(${-TIKTOK_SHIFT} ${-TIKTOK_SHIFT})`],
        ['var(--brand-tiktok-red)', `translate(${TIKTOK_SHIFT} ${TIKTOK_SHIFT})`],
        ['var(--brand-tiktok-note)', undefined],
      ] as const).map(([stroke, transform]) => (
        <g key={stroke} stroke={stroke} transform={transform}>
          {TIKTOK_NOTE.map((d) => <path key={d} d={d} />)}
        </g>
      ))}
    </g>
  )
}

/** Pięć przystanków gradientu Instagrama, od żółci w lewym dolnym rogu po fiolet. */
const INSTAGRAM_STOPS = [0, 25, 50, 75, 100]

function Instagram({ gradientId }: { gradientId: string }) {
  return (
    <>
      <defs>
        <linearGradient id={gradientId} x1="6" y1="42" x2="42" y2="6" gradientUnits="userSpaceOnUse">
          {INSTAGRAM_STOPS.map((offset, i) => (
            <stop key={offset} offset={`${offset}%`} style={{ stopColor: `var(--brand-instagram-${i + 1})` }} />
          ))}
        </linearGradient>
      </defs>
      <rect x="6" y="6" width="36" height="36" rx="10.5" fill={`url(#${gradientId})`} />
      <g fill="none" stroke="var(--brand-instagram-lens)" strokeWidth={3}>
        <rect x="13" y="13" width="22" height="22" rx="6.5" />
        <circle cx="24" cy="24" r="5.5" />
      </g>
      <circle cx="31" cy="17" r="2" fill="var(--brand-instagram-lens)" />
    </>
  )
}

function YouTube() {
  return (
    <>
      <rect x="5" y="11" width="38" height="26" rx="8" fill="var(--brand-youtube)" />
      <path d="M20.5 17.5v13l11-6.5z" fill="var(--brand-youtube-play)" />
    </>
  )
}

export interface PlatformLogoProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: PlatformLogoName
  /** Bok znaku w px. Bez niego znak wypełnia rodzica (grafiki strony publicznej). */
  size?: number
  /** Etykieta dostępna, gdy znak stoi sam. Bez niej znak jest dekoracją (aria-hidden). */
  label?: string
  /** Konto rozłączone: ten sam znak z obniżonym nasyceniem. */
  muted?: boolean
}

export function PlatformLogo({ name, size, label, muted, className = '', ...rest }: PlatformLogoProps) {
  // `useId` daje każdemu znakowi własny gradient: dwa Instagramy na jednym ekranie
  // dzieliłyby jeden `id`, a wtedy pierwszy odmontowany zabiera kolor drugiemu.
  const gradientId = `pl-ig-${useId().replace(/:/g, '')}`
  const cls = ['pl-logo', `pl-logo--${name}`, muted && 'pl-logo--muted', className].filter(Boolean).join(' ')
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 48 48"
      width={size ?? '100%'}
      height={size ?? '100%'}
      fill="none"
      className={cls}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      data-glyph={name}
      data-platform-logo={name}
      {...rest}
    >
      {name === 'tiktok' && <TikTok />}
      {name === 'instagram' && <Instagram gradientId={gradientId} />}
      {name === 'youtube' && <YouTube />}
    </svg>
  )
}

export default PlatformLogo
